import React, { useRef } from 'react';
import "./styles/styles.css";

const Dashboard = ({ onCreateNewProject, onUploadExistingProject, setCurrentPage, onLogout, navigateToPage }) => {
    const fileInputRef = useRef(null);




    // ✅ Handles JSON Upload and Restores Project Data
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const projectData = JSON.parse(e.target.result);
                //console.log("Loaded Project Data:", projectData);

                // Ensure the uploaded file has required fields
                if (projectData.projectName && projectData.startDate) {
                    const updatedProjectData = {
                        ...projectData,
                        currentPage: projectData.currentPage || "wizard", // ✅ Restore page
                        currentStep: projectData.currentStep > 1 ? projectData.currentStep : 1 // ✅ Restore last step
                    };

                    //console.log("Restoring formData:", updatedProjectData);

                    // ✅ Update formData in the parent state
                    onUploadExistingProject(updatedProjectData);

                    // ✅ Navigate to the last saved page
                    navigateToPage(updatedProjectData.currentPage);
                } else {
                    alert("The uploaded file is not in the correct Soft Estimate format.");
                }
            } catch (error) {
                alert("Invalid file format. Please upload a valid JSON file.");
                //console.error("File Upload Error:", error);
            }
        };
        reader.readAsText(file);
    };

    // ✅ Triggers the File Input Click
    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    // ✅ Handles Logout
    const handleLogout = () => {
        localStorage.removeItem("isSubscribed"); // ✅ Remove subscription session
        localStorage.removeItem("currentPage");  // ✅ Remove stored page state
        window.location.reload(); // ✅ Force refresh to apply changes
    };

    return (
        <div className="container">
            <div className="w-full flex justify-center">
                <div className="max-w-4xl w-full bg-white shadow-2xl rounded-3xl p-10">
                    <h1 className="text-4xl font-extrabold text-center mb-4">
                        <span className="text-blue-900">Welcome to Soft Estimate</span>
                    </h1>
                    <p className="text-gray-800 text-lg text-center mb-8">
                        SoftEstimate helps you quickly estimate time, cost, and resources for any project with ease. Built on industry-standard SDLC principles, it adapts to any industry, ensuring flexibility and precision. Optimize resources, eliminate waste, and stay in control—all while keeping your data private.
                    </p>

                    <div className="flex flex-col items-center space-y-4 mb-8">
                        {/* ✅ Create New Project Button */}
                        <button
                            onClick={onCreateNewProject}
                            className="w-3/4 py-4 bg-green-600 text-white rounded-xl shadow-lg hover:bg-green-700 transition-transform transform hover:scale-105"
                        >
                            Create New Project
                        </button>

                        {/* ✅ Upload Existing Project Button */}
                        <button
                            onClick={triggerFileInput}
                            className="w-3/4 py-4 bg-blue-600 text-white rounded-xl shadow-lg hover:bg-blue-700 transition-transform transform hover:scale-105"
                        >
                            Upload Existing Project
                        </button>

                        {/* ✅ Logout Button */}
                        <button
                            onClick={handleLogout}
                            className="w-3/4 py-4 bg-red-500 text-white rounded-xl shadow-lg hover:bg-red-700 transition-transform transform hover:scale-105"
                        >
                            Logout and Go to Home Page
                        </button>

                        {/* ✅ Hidden File Input for Upload */}
                        <input
                            type="file"
                            accept="application/json"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                    </div>

                    {/* ✅ Features Section */}
                    <div className="key-features-container">
                        <h3 className="key-features-title">Key Features</h3>
                        <ul className="key-features-list">
                            <li className="key-feature-item">
                                <span className="feature-icon">🚀</span>
                                <span className="feature-text"><strong>Instant Estimates:</strong> Get accurate time, cost, and resource estimates instantly.</span>
                            </li>
                            <li className="key-feature-item">
                                <span className="feature-icon">📊</span>
                                <span className="feature-text"><strong>Smart Resource Allocation:</strong> Optimize workload across teams for maximum efficiency.</span>
                            </li>
                            <li className="key-feature-item">
                                <span className="feature-icon">💰</span>
                                <span className="feature-text"><strong>Real-Time Budget Management:</strong> Adjust budgets dynamically with detailed cost breakdowns.</span>
                            </li>
                            <li className="key-feature-item">
                                <span className="feature-icon">🔄</span>
                                <span className="feature-text"><strong>Agile & Adaptive Planning:</strong> Supports Agile, Waterfall, or hybrid project methodologies.</span>
                            </li>
                            <li className="key-feature-item">
                                <span className="feature-icon">🔒</span>
                                <span className="feature-text"><strong>Complete Data Privacy:</strong> No cloud storage or tracking—your data stays with you.</span>
                            </li>
                            <li className="key-feature-item">
                                <span className="feature-icon">📁</span>
                                <span className="feature-text"><strong>Effortless Export & Integration:</strong> Export CSV files for Jira, Trello, and other tools.</span>
                            </li>
                            <li className="key-feature-item">
                                <span className="feature-icon">⚡</span>
                                <span className="feature-text"><strong>Customizable Resource Planning:</strong> Tailor resource assignments and workloads flexibly.</span>
                            </li>                            
                            <li className="key-feature-item">
                                <span className="feature-icon">🚨</span>
                                <span className="feature-text"><strong>Proactive Risk Analysis:</strong> Identify bottlenecks and resource gaps before issues arise.</span>
                            </li>
                            <li className="key-feature-item">
                                <span className="feature-icon">📈</span>
                                <span className="feature-text"><strong>Actionable Business Insights:</strong> Enable fast, data-driven decisions for business success.</span>
                            </li>
                        </ul>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Dashboard;
